import type { SettingsSectionId } from "~/services/products/settingsSections.ts";
import type { CapabilityUrlId, ChangeCapabilityUrlId, ChangeUrlId, ProductUrlId } from "~/types/entity.ts";

export const loginUrl = () => "/login";

export const collabTokenUrl = () => "/api/collab/token";

/** SSE endpoint the Converse surface POSTs a message to. */
export const changeConverseUrl = ({ changeUrlId }: { changeUrlId: ChangeUrlId }) =>
	`/api/change/${changeUrlId}/converse`;

/** Fire-and-forget endpoint that runs a reconcile checkpoint (e.g. on leaving the delta editor). */
export const changeReconcileUrl = ({ changeUrlId }: { changeUrlId: ChangeUrlId }) =>
	`/api/change/${changeUrlId}/reconcile`;

/**
 * One turn of the pre-creation exploration conversation. PRODUCT-scoped, not
 * change-scoped: exploration has no change yet (design decision 6).
 */
export const productExploreUrl = ({ productUrlId }: { productUrlId: ProductUrlId }) =>
	`/api/product/${productUrlId}/explore`;

/**
 * The sub-exploration surface's single endpoint (seed / turn /
 * summarize-and-continue / conclude / abandon, plus a GET for resumable crashed
 * sessions). PRODUCT-scoped like exploration rather than change-scoped: a branch
 * can hang off a pre-creation exploration, where no change exists yet.
 */
export const productSubExploreUrl = ({ productUrlId }: { productUrlId: ProductUrlId }) =>
	`/api/product/${productUrlId}/subexplore`;

/**
 * Detect + preview a connected repo's openspec directory. `dir` is the author's
 * override; omit it to probe the default convention path.
 */
export const repoOpenspecPreviewUrl = ({ repoId, dir }: { repoId: number; dir?: string }) => {
	const params = new URLSearchParams({ repoId: String(repoId) });
	if (dir) params.set("dir", dir);
	return `/api/repo/openspec?${params.toString()}`;
};

export const dashboardUrl = () => "/";

/**
 * Organization-level settings — the active organization's own configuration, as
 * opposed to `productSettingsUrl`, which configures one product inside it.
 */
export const orgSettingsUrl = () => "/settings";

/** The sysadmin console — global, not org-scoped. Allowlisted operators only. */
export const adminUrl = () => "/admin";

/**
 * The console's merge → reconcile view: merges still awaiting reconcile, over the
 * recorded event timeline. Filters live in search params so a narrowed view can
 * be linked to, which is why they are parameters here rather than component state.
 */
export const adminReconcileUrl = ({ change, repo }: { change?: string; repo?: string } = {}) => {
	const params = new URLSearchParams();
	if (change) params.set("change", change);
	if (repo) params.set("repo", repo);
	const query = params.toString();
	return `${adminUrl()}/reconcile${query ? `?${query}` : ""}`;
};

export const productCreateUrl = () => "/products/create";

export const productUrl = ({ productUrlId }: { productUrlId: ProductUrlId }) => `/products/${productUrlId}`;

export const productSpecsUrl = ({ productUrlId }: { productUrlId: ProductUrlId }) =>
	`${productUrl({ productUrlId })}/specs`;

export const capabilityCreateUrl = ({ productUrlId }: { productUrlId: ProductUrlId }) =>
	`${productSpecsUrl({ productUrlId })}/new`;

export const capabilityUrl = ({
	productUrlId,
	capabilityUrlId,
}: {
	productUrlId: ProductUrlId;
	capabilityUrlId: CapabilityUrlId;
}) => `${productSpecsUrl({ productUrlId })}/${capabilityUrlId}`;

export const capabilityDownloadUrl = ({
	productUrlId,
	capabilityUrlId,
}: {
	productUrlId: ProductUrlId;
	capabilityUrlId: CapabilityUrlId;
}) => `${capabilityUrl({ productUrlId, capabilityUrlId })}/download`;

export const productChangesUrl = ({ productUrlId }: { productUrlId: ProductUrlId }) =>
	`${productUrl({ productUrlId })}/changes`;

export const changeCreateUrl = ({ productUrlId }: { productUrlId: ProductUrlId }) =>
	`${productChangesUrl({ productUrlId })}/new`;

export const changeUrl = ({ productUrlId, changeUrlId }: { productUrlId: ProductUrlId; changeUrlId: ChangeUrlId }) =>
	`${productChangesUrl({ productUrlId })}/${changeUrlId}`;

export const changeProposalUrl = ({
	productUrlId,
	changeUrlId,
}: {
	productUrlId: ProductUrlId;
	changeUrlId: ChangeUrlId;
}) => `${changeUrl({ productUrlId, changeUrlId })}/proposal`;

export const changeSpecsUrl = ({
	productUrlId,
	changeUrlId,
}: {
	productUrlId: ProductUrlId;
	changeUrlId: ChangeUrlId;
}) => `${changeUrl({ productUrlId, changeUrlId })}/specs`;

export const changeDeltaUrl = ({
	productUrlId,
	changeUrlId,
	changeCapabilityUrlId,
}: {
	productUrlId: ProductUrlId;
	changeUrlId: ChangeUrlId;
	changeCapabilityUrlId: ChangeCapabilityUrlId;
}) => `${changeSpecsUrl({ productUrlId, changeUrlId })}/${changeCapabilityUrlId}`;

export const changeDesignUrl = ({
	productUrlId,
	changeUrlId,
}: {
	productUrlId: ProductUrlId;
	changeUrlId: ChangeUrlId;
}) => `${changeUrl({ productUrlId, changeUrlId })}/design`;

export const changeMockupsUrl = ({
	productUrlId,
	changeUrlId,
}: {
	productUrlId: ProductUrlId;
	changeUrlId: ChangeUrlId;
}) => `${changeUrl({ productUrlId, changeUrlId })}/mockups`;

/**
 * The standalone mockup-editing session — a change-scoped surface for working on
 * the change's mockup copy directly, outside the drafting conversation.
 */
export const changeMockupSessionUrl = ({
	productUrlId,
	changeUrlId,
}: {
	productUrlId: ProductUrlId;
	changeUrlId: ChangeUrlId;
}) => `${changeMockupsUrl({ productUrlId, changeUrlId })}/session`;

export const changeTasksUrl = ({
	productUrlId,
	changeUrlId,
}: {
	productUrlId: ProductUrlId;
	changeUrlId: ChangeUrlId;
}) => `${changeUrl({ productUrlId, changeUrlId })}/tasks`;

export const changeReviewUrl = ({
	productUrlId,
	changeUrlId,
}: {
	productUrlId: ProductUrlId;
	changeUrlId: ChangeUrlId;
}) => `${changeUrl({ productUrlId, changeUrlId })}/review`;

export const changeReviewCapabilityUrl = ({
	productUrlId,
	changeUrlId,
	changeCapabilityUrlId,
}: {
	productUrlId: ProductUrlId;
	changeUrlId: ChangeUrlId;
	changeCapabilityUrlId: ChangeCapabilityUrlId;
}) => `${changeReviewUrl({ productUrlId, changeUrlId })}/${changeCapabilityUrlId}`;

/**
 * Settings shows one section at a time. Omit `section` for the tab link itself —
 * the page resolves an absent parameter to its first section, so the bare path
 * stays the canonical entry point.
 */
export const productSettingsUrl = ({
	productUrlId,
	section,
}: {
	productUrlId: ProductUrlId;
	section?: SettingsSectionId;
}) => `${productUrl({ productUrlId })}/settings${section ? `?section=${section}` : ""}`;

/**
 * The product's GitHub section, addressed as a section of its own rather than as
 * a card someone has to find inside Settings.
 *
 * Expressed through `productSettingsUrl` rather than as a second literal path:
 * the section still renders where it always did, and a helper that wrote its own
 * `/github` path would be a promise the router does not keep.
 */
export const productGithubUrl = ({ productUrlId }: { productUrlId: ProductUrlId }) =>
	productSettingsUrl({ productUrlId, section: "github" });

export const changeExportUrl = ({
	productUrlId,
	changeUrlId,
}: {
	productUrlId: ProductUrlId;
	changeUrlId: ChangeUrlId;
}) => `${changeUrl({ productUrlId, changeUrlId })}/export`;

/** External GitHub App install URL carrying the signed state token. */
export const githubInstallUrl = ({ appSlug, state }: { appSlug: string; state: string }) =>
	`https://github.com/apps/${appSlug}/installations/new?state=${encodeURIComponent(state)}`;
